import { useQuery } from "@apollo/client";
import {
  useGetHomeDataAdminLazyQuery,
  useGetHomeDataCoachLazyQuery,
  useGetHomeDataManagerLazyQuery,
  useGetHomeDataStudentLazyQuery,
  useGetResourcesLazyQuery,
} from "graphql/__generated__/hooks";
import {
  GET_HOME_COACH_ACTIVITY,
  GET_HOME_LEARNER_ACTIVITY,
} from "graphql/home/home.query";
import { useAuthUser } from "hooks/useAuthUser";
import { useEffect, useMemo, useState } from "react";
import { ROLES } from "types/user";
import { organizationSelectedSelector } from "reduxStore/organization/selectors";
import { useAppSelector } from "hooks/useAppSelector";
import { REQUIREMENT } from "../../../helpers/constants";
import { filterFeedbackStats, getFeedbackForChart } from "utils/feedback";
import { Resource } from "../../../types/resource";
import { SPECIAL_TAGS } from "../../../types/tag";
import {
  DEFAULT_METRICS_DATE_RANGE,
  getMetricsSinceDate,
  MetricsDateRange,
} from "pages/Metrics/metricsDateRange";
import { isTestOrganizationName, excludeTestOrganizationVariables } from "utils/testOrganization";

export const useHome = () => {
  const { user } = useAuthUser();
  const organization = useAppSelector(organizationSelectedSelector);
  const [allResources, setAllResources] = useState([] as Resource[]);
  const isStudent = user?.role === ROLES.STUDENT;
  const isCoach = user?.role === ROLES.COACH;

  const [getHomeDataCoach, { data: dataCoach, loading: loadingCoach }] =
    useGetHomeDataCoachLazyQuery();

  const [getHomeDataStudent, { data: dataStudent, loading: loadingStudent }] =
    useGetHomeDataStudentLazyQuery();

  const { data: learnerActivity, loading: loadingLearnerActivity } = useQuery(
    GET_HOME_LEARNER_ACTIVITY,
    {
      variables: { userId: user?.user_id },
      skip: !isStudent || !user?.user_id,
      fetchPolicy: "cache-and-network",
    },
  );

  const { data: coachActivity, loading: loadingCoachActivity } = useQuery(
    GET_HOME_COACH_ACTIVITY,
    {
      variables: { userId: user?.user_id },
      skip: !isCoach || !user?.user_id,
      fetchPolicy: "cache-and-network",
    },
  );

  const [getHomeDataAdmin, { data: dataAdmin }] =
    useGetHomeDataAdminLazyQuery();
  const [getHomeDataManager, { data: dataManager }] =
    useGetHomeDataManagerLazyQuery();
  const [getAllResources, { data: dataResources }] = useGetResourcesLazyQuery();

  const [data, setData] = useState<any>(null);
  const [dateRange, setDateRange] = useState<MetricsDateRange>(
    DEFAULT_METRICS_DATE_RANGE,
  );
  const sinceDate = useMemo(() => getMetricsSinceDate(dateRange), [dateRange]);
  const loading = useMemo(() => {
    if (isStudent) {
      return (
        (loadingStudent || loadingLearnerActivity) &&
        !dataStudent &&
        !learnerActivity
      );
    }
    if (isCoach) {
      return (
        (loadingCoach || loadingCoachActivity) &&
        !dataCoach &&
        !coachActivity
      );
    }
    return loadingCoach || loadingStudent;
  }, [
    isStudent,
    isCoach,
    loadingCoach,
    loadingStudent,
    loadingLearnerActivity,
    loadingCoachActivity,
    dataStudent,
    dataCoach,
    learnerActivity,
    coachActivity,
  ]);

  const [statsData, setStatsData] = useState<any>(null);

  useEffect(() => {
    setAllResources((dataResources?.resource as Resource[]) || []);
  }, [dataResources]);

  useEffect(() => {
    if (user?.role === ROLES.COACH) {
      getHomeDataCoach({
        variables: {
          userId: user?.user_id!,
          sinceDate,
          ...excludeTestOrganizationVariables,
        },
        fetchPolicy: "cache-and-network",
      }).then();
    } else if (user?.role === ROLES.STUDENT) {
      getAllResources().then();
      getHomeDataStudent({
        variables: {
          userId: user?.user_id!,
          sinceDate,
          ...excludeTestOrganizationVariables,
        },
        fetchPolicy: "cache-and-network",
      }).then();
    } else if (user?.role === ROLES.ADMIN) {
      getHomeDataAdmin({
        variables: { sinceDate, ...excludeTestOrganizationVariables },
      }).then();
    }
  }, [user, sinceDate]);

  useEffect(() => {
    if (
      (user?.role === ROLES.MANAGER || user?.role === ROLES.DIRECTOR) &&
      organization?.org_id &&
      !isTestOrganizationName(organization.name)
    ) {
      getHomeDataManager({
        variables: {
          orgId: organization.org_id!,
          sinceDate,
          ...excludeTestOrganizationVariables,
        },
      });
    }
  }, [user, organization, sinceDate]);

  useEffect(() => {
    if (dataCoach || (isCoach && coachActivity)) {
      setData({
        ...(dataCoach || {}),
        pendingFeedbacks:
          coachActivity?.pendingFeedbacks ?? dataCoach?.pendingFeedbacks,
        feedbacks: coachActivity?.feedbacks ?? dataCoach?.feedbacks,
      });
      setStatsData(dataCoach?.feedbackStatsData);
    } else if (dataStudent || (isStudent && learnerActivity)) {
      // Data Student
      const reqs =
        dataStudent?.graduation_class?.[0]?.current_academic_year?.requirements?.map(
          (r) => r.requirement,
        ) || [];
      const epas = reqs.filter(
        (req) => req?.type === REQUIREMENT.REQUIREMENT_EPA,
      );
      const competencies = reqs.filter(
        (req) => req?.type === REQUIREMENT.REQUIREMENT_COMPETENCY,
      );
      const iters = reqs.filter(
        (req) => req?.type === REQUIREMENT.REQUIREMENT_ITER,
      );
      const requirementsResources = reqs
        .map((r) => r.resources?.map((c) => c.resource) || [])
        .flat();
      const filteredResources = allResources.filter((resource) =>
        [organization?.name, SPECIAL_TAGS.QUICK_ACCESS].every((tagName) =>
          resource.tags?.some(
            (tagContainer) => tagContainer.tag.name === tagName,
          ),
        ),
      );

      const resources = [...requirementsResources, ...filteredResources].filter(
        (resource, index, self) =>
          index ===
          self.findIndex((r) => r.resource_id === resource.resource_id),
      );

      const feedbacksApproved: Record<string, number> = {};
      dataStudent?.feedbacksApproved?.forEach((f) => {
        if (!feedbacksApproved[f.feedback_skills[0]?.requirement_id]) {
          feedbacksApproved[f.feedback_skills[0]?.requirement_id] =
            f.feedback_skills[0]?.coach_rating || -1;
        }
      });

      const feedbacksByRequirementId: Record<string, any[]> = {};
      dataStudent?.feedbackStatsData?.forEach((feedback) => {
        if (
          !feedbacksByRequirementId[feedback.feedback_skills[0]?.requirement_id]
        ) {
          feedbacksByRequirementId[
            feedback.feedback_skills[0]?.requirement_id
          ] = [feedback];
        } else {
          feedbacksByRequirementId[
            feedback.feedback_skills[0]?.requirement_id
          ].push(feedback);
        }
      });

      const feedbackStatsByRequirementId: Record<string, any> = {};
      Object.keys(feedbacksByRequirementId).forEach((key) => {
        const filteredFeedbackStats = filterFeedbackStats(
          feedbacksByRequirementId[key],
          true,
        );
        feedbackStatsByRequirementId[key] = {
          totalFeedbacks: filteredFeedbackStats.length,
          currentRate: Math.max(
            ...filteredFeedbackStats.map((stat) => stat.value),
            0,
          ),
          data: getFeedbackForChart(filteredFeedbackStats),
        };
      });
      // Aggregate score progress per ITER (feedbacks whose skills are all under this ITER's competencies)
      iters.forEach((iter: any) => {
        const competencyIds =
          iter.competencies?.map(
            (c: any) => c.competency?.requirement_id,
          ).filter(Boolean) || [];
        if (competencyIds.length === 0) return;
        const feedbacksForIter = (dataStudent?.feedbackStatsData ?? []).filter(
          (f: any) =>
            f.feedback_skills?.length > 0 &&
            f.feedback_skills.every((s: any) =>
              competencyIds.includes(s.requirement_id),
            ),
        );
        if (feedbacksForIter.length === 0) return;
        const filteredFeedbackStats = filterFeedbackStats(
          feedbacksForIter as any,
          true,
        );
        const rates = filteredFeedbackStats.map((s: any) => s.value);
        feedbackStatsByRequirementId[iter.requirement_id] = {
          totalFeedbacks: filteredFeedbackStats.length,
          currentRate: rates.length > 0 ? Math.max(...rates) : 0,
          data: getFeedbackForChart(filteredFeedbackStats),
        };
        feedbacksByRequirementId[iter.requirement_id] = feedbacksForIter;
      });
      const mentors =
        dataStudent?.mentors?.map((mentor: any) => mentor.mentor) || [];

      // Calculate completion rate for learners (EPA + Competency)
      // Total = sum of all min_performance (use 1 if null) of EPA and Competency requirements
      const totalEPAAttemptsRequired = epas.reduce((sum, epa) => {
        return sum + (epa.min_performance || 1);
      }, 0);

      const totalCompetencyAttemptsRequired = competencies.reduce(
        (sum, comp) => {
          return sum + (comp.min_performance || 1);
        },
        0,
      );

      const totalAttemptsRequired =
        totalEPAAttemptsRequired + totalCompetencyAttemptsRequired;

      // Completed = sum per requirement of min(accepted feedback count for that requirement, requirement.min_performance)
      const requirementsWithMin = [
        ...epas.map((epa) => ({
          requirement_id: epa.requirement_id,
          min: epa.min_performance || 1,
        })),
        ...competencies.map((comp) => ({
          requirement_id: comp.requirement_id,
          min: comp.min_performance || 1,
        })),
      ];

      const acceptedCountByRequirementId: Record<string, number> = {};
      dataStudent?.feedbackStatsData?.forEach((feedback: any) => {
        if (feedback.status !== "DONE") return;
        const rid = feedback.feedback_skills?.[0]?.requirement_id;
        if (!rid) return;
        acceptedCountByRequirementId[rid] =
          (acceptedCountByRequirementId[rid] ?? 0) + 1;
      });
      const attemptsCompleted = requirementsWithMin.reduce(
        (sum, { requirement_id, min }) => {
          const accepted = acceptedCountByRequirementId[requirement_id] ?? 0;
          return sum + Math.min(accepted, min);
        },
        0,
      );

      // Use at least the actual feedback count so "Total Attempts Required" matches what they see in all feedbacks
      const totalForDisplay = Math.max(totalAttemptsRequired, 1);
      const completedForDisplay = Math.min(attemptsCompleted, totalForDisplay);

      const epaCompletionRate =
        totalForDisplay > 0
          ? {
              completed: completedForDisplay,
              total: totalForDisplay,
              displayText: `${completedForDisplay}/${totalForDisplay}`,
            }
          : undefined;

      setData({
        feedbacks:
          learnerActivity?.feedbacks ?? dataStudent?.feedbacks,
        pendingFeedbacks:
          learnerActivity?.pendingFeedbacks ?? dataStudent?.pendingFeedbacks,
        competencyRequirements: competencies,
        epaRequirements: epas,
        iterRequirements: iters,
        allRequirements: reqs, // Include all requirements (EPA, Competency, ITER) for finding parent ITER
        resources,
        feedbacksApproved,
        feedbacksByRequirementId,
        feedbackStatsByRequirementId,
        mentors,
        epaCompletionRate,
      });
      setStatsData(dataStudent?.feedbackStatsData);
    } else if (dataAdmin) {
      setData(dataAdmin);
      setStatsData(dataAdmin?.feedbackStatsData);
    } else if (dataManager) {
      if (isTestOrganizationName(organization?.name)) {
        setData(null);
        setStatsData(null);
      } else {
        setData(dataManager);
        setStatsData(dataManager?.feedbackStatsData);
      }
    }
  }, [
    dataCoach,
    dataStudent,
    dataAdmin,
    dataManager,
    allResources,
    organization?.name,
    learnerActivity,
    coachActivity,
    isStudent,
    isCoach,
  ]);

  return { data, loading, user, organization, statsData, dateRange, setDateRange };
};
